import { IconLayoutGrid, IconPlus } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button, Form, Grid, Input, Modal, Space, theme } from "antd";
import { useState } from "react";
import { useOrganizationMetrics } from "@/common/artemis-operations/organization";
import {
	useCreateWorkspace,
	useWorkspaces,
} from "@/common/artemis-operations/workspaces";
import {
	BaseContainer,
	CustomEmpty,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { CardBody, CardViewer } from "@/components/card-viewer";
import { SidebarIdentity } from "@/components/layout/workspace/sidebar-identity";
import { WizardsProvider } from "@/contexts/wizard-context";

export const Route = createFileRoute("/_authenticated/$orgSlug/workspaces/")({
	component: Workspaces,
	// Carries a pending gallery import through the workspace picker when the
	// `/import-template` deep link couldn't resolve a single workspace on its own.
	validateSearch: (
		search: Record<string, unknown>,
	): { importTemplate?: string } => ({
		importTemplate:
			typeof search.importTemplate === "string"
				? search.importTemplate
				: undefined,
	}),
});

function Workspaces() {
	const navigate = useNavigate();
	const { orgSlug } = Route.useParams();
	const { importTemplate } = Route.useSearch();
	const { data: workspaces, isPending, isError } = useWorkspaces();
	const { mutateAsync: createWorkspace, isPending: creating } =
		useCreateWorkspace();
	const [openCreate, setOpenCreate] = useState(false);
	const [form] = Form.useForm<{ name: string }>();
	const isMobile = !Grid.useBreakpoint().md;
	const {
		token: { colorBgLayout },
	} = theme.useToken();

	const rows = (workspaces ?? []).filter(
		(ws): ws is NonNullable<typeof ws> => ws != null,
	);

	const { data: metrics } = useOrganizationMetrics();
	const quotaReached =
		metrics?.maxWorkspaces != null &&
		metrics.workspaceCount >= metrics.maxWorkspaces;

	const handleCreate = async () => {
		const values = await form.validateFields();
		await createWorkspace({ name: values.name });
		form.resetFields();
		setOpenCreate(false);
	};

	const handleCancel = () => {
		setOpenCreate(false);
		form.resetFields();
	};

	return (
		<WizardsProvider>
			<div
				style={{
					minHeight: "100vh",
					background: colorBgLayout,
					position: "relative",
				}}
			>
				{/* Borderless account menu in the corner — this page has no sidebar shell. */}
				<div style={{ position: "absolute", top: 12, right: 16 }}>
					<SidebarIdentity minimal />
				</div>
				<div
					style={{
						maxWidth: 1100,
						margin: "0 auto",
						padding: isMobile ? "24px 16px" : "40px 24px",
					}}
				>
					<BaseContainer
						title="Workspaces"
						description="Select a workspace to enter, or create a new one."
					>
						<QueryGuard isPending={isPending} isError={isError} isNull={false}>
							{rows.length === 0 ? (
								<CustomEmpty description="workspaces" expanded>
									<Button
										type="primary"
										disabled={quotaReached}
										title={"Workspace"}
										onClick={() => setOpenCreate(true)}
									>
										Create Workspace
									</Button>
								</CustomEmpty>
							) : (
								<CardViewer
									items={rows}
									getKey={(w) => w.id}
									onOpen={(w) =>
										navigate(
											importTemplate
												? {
														to: "/$orgSlug/workspaces/$workspaceId/task-templates",
														params: { orgSlug, workspaceId: w.id },
														search: { importTemplate },
													}
												: {
														to: "/$orgSlug/workspaces/$workspaceId",
														params: { orgSlug, workspaceId: w.id },
													},
										)
									}
									searchText={(w) => w.name}
									searchPlaceholder="Search workspaces"
									extra={
										<Button
											type="default"
											icon={<CustomIcon icon={IconPlus} />}
											disabled={quotaReached}
											title={"Workspace"}
											onClick={() => setOpenCreate(true)}
										>
											New workspace
										</Button>
									}
									renderCard={(w) => (
										<CardBody
											title={w.name}
											extra={<CustomIcon icon={IconLayoutGrid} />}
										/>
									)}
								/>
							)}
						</QueryGuard>
					</BaseContainer>
				</div>

				<Modal
					open={openCreate}
					title="New workspace"
					onCancel={handleCancel}
					footer={
						<Space>
							<Button onClick={handleCancel}>Cancel</Button>
							<Button type="primary" loading={creating} onClick={handleCreate}>
								Create
							</Button>
						</Space>
					}
				>
					<Form form={form} layout="vertical" style={{ marginTop: 16 }}>
						<Form.Item
							name="name"
							label="Name"
							rules={[{ required: true, message: "Name is required" }]}
						>
							<Input placeholder="My workspace" autoFocus />
						</Form.Item>
					</Form>
				</Modal>
			</div>
		</WizardsProvider>
	);
}
